import { Injectable, SecurityContext } from '@angular/core';
import { FiImageSize, FileType, ObjectTypes, SortType } from '../types';
import { ISiteMap, ISiteMapContentItem, ISiteMapContentItemViewModel, ISiteMapTag } from '../models/site-map';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { fiTextToHtml, getObjectTypeNameTextId, isDefined } from '../utils';
import { capitalize } from '@angular-devkit/core/src/utils/strings';
import { AppTextService } from '../app-text';
import { FiDialogsService } from '../fi-dialogs';
import { IFiDialogResult } from '../fi-dialogs/fi-dialogs';
import { AccessRightFilterCollectionModel } from '../models';
import { SiteMapDndType } from '../types/site-map-dnd-types';
import { FiAppSettings } from '../fi-app-settings';
import { FiMediaStorageService } from '../media-bank/fi-media-storage.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ProtocolIndependentPipe } from '../fi-pipes/protocol-independent.pipe';

@Injectable()
export class SiteMapHelperService {
    constructor(
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private fiMediaStorageService: FiMediaStorageService,
        private domSanitizer: DomSanitizer,
        private protocolIndependentPipe: ProtocolIndependentPipe,
    ) {}

    private _setDataSets$ = new Subject<number>();
    triggerSetTagsDataSets(tagId: number) {
        this._setDataSets$.next(tagId);
    }
    onSetDataSets$(): Observable<number> {
        return this._setDataSets$.asObservable();
    }

    getSiteMapInstance(): ISiteMap {
        return {
            Name: undefined,
            AccessRightFilterCollection: new AccessRightFilterCollectionModel(),
            SortingType: SortType.Chronological,
        };
    }

    private readonly siteMapContentItemsTypeOrder: { [key in ObjectTypes]?: number } = {
        [ObjectTypes.Document]: 1,
        [ObjectTypes.MediaBank]: 2,
        [ObjectTypes.Tag]: 3,
    };
    private recalculateSiteMapItemDepth$ = new Subject<void>();

    doRecalculateSiteMapItemDepth() {
        this.recalculateSiteMapItemDepth$.next();
    }

    get onRecalculateSiteMapItemDepth$(): Observable<void> {
        return this.recalculateSiteMapItemDepth$.asObservable();
    }

    showUserHasNoEditAccessDialog() {
        this.fiDialogsService.error({
            messageText: this.appTextService.get('error_message.no_access_to_action'),
        });
    }

    showItemIsAlreadyTaggedDialog(movedItem: ISiteMapContentItem, targetTag: ISiteMapContentItem) {
        const objectTypeNameLabel: string = this.appTextService
            .get(getObjectTypeNameTextId(movedItem.ObjectType))
            .toLowerCase();

        this.fiDialogsService.warningConfirm({
            headerText: capitalize(
                this.appTextService.get('sitemap.content_item_tag_duplicate_warning_header', {
                    '{0}': objectTypeNameLabel,
                }),
            ),
            messageText: capitalize(
                this.appTextService.get('sitemap.content_item_tag_duplicate_warning_message', {
                    '{0}': objectTypeNameLabel,
                    '{1}': movedItem.Name,
                    '{2}': targetTag.Name,
                }),
            ),
            isNoButtonHidden: true,
            yesButtonText: this.appTextService.get('ok'),
        });
    }

    confirmContentItemTagChange(
        siteMapTagData: ISiteMapTag,
        targetTag: ISiteMapContentItem,
        movedItem: ISiteMapContentItem,
    ): IFiDialogResult {
        const objectTypeNameLabel: string = this.appTextService
            .get(getObjectTypeNameTextId(movedItem.ObjectType))
            .toLowerCase();
        const sourceTagName: string = this.findTagById(siteMapTagData, movedItem.ParentId).Name;

        return this.fiDialogsService.confirm({
            headerText: this.appTextService.get('sitemap.content_item_update_tag_header', {
                '{0}': objectTypeNameLabel,
            }),
            messageText: this.appTextService.get('sitemap.content_item_update_tag_message', {
                '{0}': objectTypeNameLabel,
                '{1}': fiTextToHtml(movedItem.Name),
                '{2}': fiTextToHtml(sourceTagName),
                '{3}': fiTextToHtml(targetTag.Name),
            }),
            headerIconClass: 'BrandedColor far fa-circle-exclamation',
            isTopCloseButtonShown: true,
            yesButtonText: this.appTextService.get('common.confirm'),
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                autoFocus: false,
            },
        });
    }

    findTagById(siteMapTagData: ISiteMapTag, tagId: number): ISiteMapContentItem {
        if (siteMapTagData.ObjectId === tagId) return siteMapTagData as ISiteMapContentItem;

        return this.findTagInSiteMap(siteMapTagData.ChildItems, tagId);
    }

    private findTagInSiteMap(items: ISiteMapContentItem[], tagId: number): ISiteMapContentItem {
        for (const item of items) {
            if (item.ObjectType === ObjectTypes.Tag && item.Id === tagId) return item;

            if (!!item.ChildItems?.length) {
                const tag: ISiteMapContentItem = this.findTagInSiteMap(item.ChildItems, tagId);
                if (isDefined(tag)) return tag;
            }
        }

        return undefined;
    }

    removeDroppedItemFromOldParent(
        droppedItem: ISiteMapContentItem,
        currentItem: { ObjectId: number; ChildItems: ISiteMapContentItem[] },
    ): boolean {
        const droppedItemIndex: number = currentItem.ChildItems.findIndex((item: ISiteMapContentItem) => {
            return (
                item.Id === droppedItem.Id &&
                item.ObjectType === droppedItem.ObjectType &&
                currentItem.ObjectId !== droppedItem.ParentId
            );
        });

        if (droppedItemIndex !== -1) {
            currentItem.ChildItems.splice(droppedItemIndex, 1);
            return true;
        }

        for (const childItem of currentItem.ChildItems) {
            const wasRemoved: boolean = this.removeDroppedItemFromOldParent(droppedItem, childItem);
            if (wasRemoved) {
                return true;
            }
        }

        return false;
    }

    getSiteMapContentItemViewData(dataSet: ISiteMapContentItem[], mainTagId: number) {
        const result: ISiteMapContentItemViewModel[] = [];
        const addDataFn = (
            items: ISiteMapContentItem[],
            parentItem: ISiteMapContentItem,
            level: number,
            parentNamePrefix: string,
        ) => {
            let childItemIndex = 0;
            items.forEach((item: ISiteMapContentItem) => {
                let fullName: string;
                let namePrefix: string;
                if (item.ObjectType === ObjectTypes.MediaBank) {
                    fullName = item.Name;
                } else {
                    childItemIndex++;
                    namePrefix = !parentNamePrefix
                        ? childItemIndex.toString()
                        : parentNamePrefix + '.' + childItemIndex.toString();
                    fullName = namePrefix + ' ' + item.Name;
                }

                const viewModel: ISiteMapContentItemViewModel = {
                    objectId: item.ObjectId,
                    objectType: item.ObjectType,
                    parentId: parentItem?.Id ?? mainTagId,
                    level: level,
                    fullName: fullName,
                };
                result.push(viewModel);
                addDataFn(item.ChildItems ?? [], item, level + 1, namePrefix);
            });
        };

        addDataFn(dataSet, undefined, 0, '');
        return result;
    }

    isObjectTypeMediaBank(objectType: ObjectTypes): boolean {
        return objectType === ObjectTypes.MediaBank;
    }

    isObjectTypeDocument(objectType: ObjectTypes): boolean {
        return objectType === ObjectTypes.Document;
    }

    isObjectTypeTag(objectType: ObjectTypes): boolean {
        return objectType === ObjectTypes.Tag;
    }

    isContentItemPreviewImageShown(isViewMode: boolean, contentItem: ISiteMapContentItem): boolean {
        return (
            isViewMode &&
            (this.isObjectTypeDocument(contentItem.ObjectType) ||
                (this.isObjectTypeMediaBank(contentItem.ObjectType) && this.isContentItemImageAvailable(contentItem)))
        );
    }

    isMediaBankPreviewIconShown(isViewMode: boolean, contentItem: ISiteMapContentItem): boolean {
        return (
            isViewMode &&
            this.isObjectTypeMediaBank(contentItem.ObjectType) &&
            !this.isContentItemImageAvailable(contentItem)
        );
    }

    isContentItemDefaultIconShown(isViewMode: boolean, contentItem: ISiteMapContentItem): boolean {
        return !isViewMode && !this.isObjectTypeTag(contentItem.ObjectType);
    }

    getDefaultItemIconClass(contentItem: ISiteMapContentItem): { [p: string]: boolean } {
        return {
            'fa-file-image': this.isObjectTypeMediaBank(contentItem.ObjectType),
            'fa-file-lines': this.isObjectTypeDocument(contentItem.ObjectType),
        };
    }

    getBackgroundImage(contentItem: ISiteMapContentItem): string {
        if (!this.isContentItemImageAvailable(contentItem)) return '';

        const fileName: string = contentItem.PreviewFileName + FiAppSettings.DefaultMediaBankImageExtension;
        const mediaFileUrlByFileName: string = this.fiMediaStorageService.getMediaFileUrlByFileName(
            fileName,
            FiImageSize.Thumbnail,
            contentItem.ChangedOn,
        );
        return this.domSanitizer.sanitize(
            SecurityContext.STYLE,
            'url("' + this.protocolIndependentPipe.transform(mediaFileUrlByFileName) + '")',
        );
    }

    isContentItemImageAvailable(contentItem: ISiteMapContentItem): boolean {
        return (
            contentItem.ObjectType !== ObjectTypes.MediaBank ||
            contentItem.FileType === FileType.Image ||
            contentItem.FileType === FileType.Pdf
        );
    }

    // note: when changing these variables, changes must be applied in styles file as well. site-map-table-item-new.component.scss
    private readonly _togglerIconWidth: number = 24;
    private readonly _togglerIconMargin: number = 8;
    private readonly _itemNamePadding: number = 16;
    //

    getSideMapContentItemPaddingLeft(itemDepth: number): string {
        return `${itemDepth * (this._togglerIconWidth + this._togglerIconMargin) + this._itemNamePadding}px`;
    }

    getSortedTagData(items: ISiteMapContentItem[], sortType: SortType): ISiteMapContentItem[] {
        if (sortType === SortType.Alphabetical) return this.sortItemsAlphabetically(items, false);

        if (sortType === SortType.ReversedAlphabetical) return this.sortItemsAlphabetically(items, true);

        if (sortType === SortType.Chronological) return this.sortItemsChronologically(items, false);

        if (sortType === SortType.ReversedChronological) return this.sortItemsChronologically(items, true);
    }

    private sortItemsAlphabetically(items: ISiteMapContentItem[], isReverse: boolean): ISiteMapContentItem[] {
        const itemsToSort: ISiteMapContentItem[] = this.getUnpinnedContentItemsToSort(
            items,
            isReverse,
            this.sortItemsAlphabetically.bind(this),
        );
        const sortedUnpinned: ISiteMapContentItem[] = this.sortContentItemByType(
            this.sortContentItemByName(itemsToSort, isReverse),
        );
        return this.insertPinnedContentItems(items, sortedUnpinned);
    }

    // reversed - new first;
    private sortItemsChronologically(items: ISiteMapContentItem[], isReverse: boolean): ISiteMapContentItem[] {
        const itemsToSort: ISiteMapContentItem[] = this.getUnpinnedContentItemsToSort(
            items,
            isReverse,
            this.sortItemsChronologically.bind(this),
        );
        const sortedUnpinned: ISiteMapContentItem[] = this.sortContentItemByType(
            this.sortContentItemByCreatedOn(itemsToSort, isReverse),
        );
        return this.insertPinnedContentItems(items, sortedUnpinned);
    }

    private getUnpinnedContentItemsToSort(
        items: ISiteMapContentItem[],
        isReverse: boolean,
        itemsSortFn: (items: ISiteMapContentItem[], isReverse: boolean) => ISiteMapContentItem[],
    ) {
        return items
            .filter((item: ISiteMapContentItem) => !item.IsPinned)
            .map((item: ISiteMapContentItem) => ({
                ...item,
                ChildItems: item.ChildItems ? itemsSortFn(item.ChildItems, isReverse) : [],
            }));
    }

    private insertPinnedContentItems(allItems: ISiteMapContentItem[], unpinnedItems: ISiteMapContentItem[]) {
        let unpinnedIndex: number = 0;
        return allItems.map((item: ISiteMapContentItem) => (item.IsPinned ? item : unpinnedItems[unpinnedIndex++]));
    }

    private sortContentItemByCreatedOn(items: ISiteMapContentItem[], isReverse: boolean): ISiteMapContentItem[] {
        return items.sort((a: ISiteMapContentItem, b: ISiteMapContentItem) => {
            const dateA: number = new Date(a.CreatedOn).getTime();
            const dateB: number = new Date(b.CreatedOn).getTime();
            return isReverse ? dateB - dateA : dateA - dateB;
        });
    }

    private sortContentItemByType(items: ISiteMapContentItem[]): ISiteMapContentItem[] {
        return items.sort((a: ISiteMapContentItem, b: ISiteMapContentItem) => {
            return this.siteMapContentItemsTypeOrder[a.ObjectType] - this.siteMapContentItemsTypeOrder[b.ObjectType];
        });
    }

    private sortContentItemByName(items: ISiteMapContentItem[], isReverse: boolean): ISiteMapContentItem[] {
        return items.sort((a: ISiteMapContentItem, b: ISiteMapContentItem) => {
            const comparison: number = a.Name.localeCompare(b.Name, undefined, { numeric: true });
            return isReverse ? -comparison : comparison;
        });
    }

    private currentDraggedSitemapType$ = new BehaviorSubject<SiteMapDndType>(undefined);
    setCurrentDraggedSitemapType(sitemapDndType: SiteMapDndType) {
        this.currentDraggedSitemapType$.next(sitemapDndType);
    }
    onCurrentDraggedSitemapTypeChange$(): Observable<SiteMapDndType> {
        return this.currentDraggedSitemapType$.asObservable();
    }

    normalizeDndIndex(previousIndex: number, currentNewIndex: number) {
        // if user drops element to position after current normalized index should be reduced by 1
        return currentNewIndex > previousIndex ? currentNewIndex - 1 : currentNewIndex;
    }
}
